import { useState, useEffect } from "react";
import { useCurrency } from "../../context/CurrencyContext";

/**
 * @param {Array} contactCards - List of contact card objects with reservation.address
 * @returns {{ currencySymbols: Object, getCurrencySymbol: Function }}
 *   currencySymbols: map of lowercased address to currency symbol string
 *   getCurrencySymbol: function that returns the symbol for a given contact
 */
function useCurrencySymbols(contactCards) {
    const [currencySymbols, setCurrencySymbols] = useState({});
    const { getCurrencySymbolByAddress } = useCurrency();

    useEffect(() => {
        loadCurrencySymbols(contactCards, getCurrencySymbolByAddress, setCurrencySymbols);
    }, [contactCards, getCurrencySymbolByAddress]);

    return { currencySymbols, getCurrencySymbol: buildGetCurrencySymbol(currencySymbols) };
}

/**
 * @param {Array} contactCards - Contact cards list
 * @param {Function} getCurrencySymbolByAddress - Currency resolver from context
 * @param {Function} setCurrencySymbols - State setter
 * @returns {Promise<void>}
 */
async function loadCurrencySymbols(contactCards, getCurrencySymbolByAddress, setCurrencySymbols) {
    if (!contactCards || contactCards.length === 0) return;

    const symbols = {};
    const uniqueAddresses = new Map();

    contactCards.forEach(contact => {
        if (contact?.reservation?.address) {
            const key = contact.reservation.address.toLowerCase();
            if (!uniqueAddresses.has(key)) {
                uniqueAddresses.set(key, contact.reservation.address);
            }
        }
    });

    for (const [key, address] of uniqueAddresses) {
        try {
            const symbol = await getCurrencySymbolByAddress(address);
            symbols[key] = symbol || "";
        } catch (error) {
            console.error(`Error loading currency for ${key}:`, error);
            symbols[key] = "";
        }
    }

    setCurrencySymbols(symbols);
}

/**
 * @param {Object} currencySymbols - Map of address keys to symbols
 * @returns {Function} A function that returns the currency symbol for a contact
 */
function buildGetCurrencySymbol(currencySymbols) {
    return (contact) => {
        if (!contact?.reservation?.address) return "";
        const key = contact.reservation.address.toLowerCase();
        return currencySymbols[key] || "";
    };
}

export default useCurrencySymbols;
